{
  "title": "How to manage event members and the organizer console",
  "description": "Add event members and roles, then use the organizer console for overview, notices and email push, help desk, meal export and operations tools.",
  "headings": [
    "What you will accomplish",
    "Who this is for",
    "Before you start",
    "Steps",
    "Afterward",
    "FAQ"
  ],
  "summary": "When done every event staff member has least-privilege access, and the organizer can publish notices, work cases, manage volunteers and posters, and watch shuttle bookings and check-ins live.",
  "roleNote": "Owner (including platform super admins) can do everything; Manager handles notices, email push, meal export and cases; Scanner checks in and sees the passenger list; Viewer only views reports",
  "prerequisites": [
    "You are an Owner of the event, or a platform super admin",
    "The email of each member to add (you can add them early; it takes effect once they register and verify their email)"
  ],
  "pathLabel": "Account menu → Event admin → the event, or /events/:slug/admin; operations tools are at /events/:slug/admin/operations",
  "steps": [
    {
      "title": "1. Open the organizer console",
      "description": "If you are event staff, the account menu at the top right shows Event admin; open the event's console (one entry per event, up to 5). The console is split into tabs by your capabilities: Overview, Notices, Help desk, Members. With only one tab available the tab bar is hidden, and the current tab is kept in the tab URL parameter."
    },
    {
      "title": "2. Add members and assign roles",
      "description": "On the Members tab enter an email and pick a role: Owner (everything including members), Manager (notices, email push, meal export, cases), Scanner (check-in and passenger list), Viewer (reports only). Emails are lower-cased and de-duplicated, with at most 50 members per event. Someone not yet registered shows as Pending and becomes Active after registering and verifying their email. You can change a role or remove a member; the last Owner cannot be removed or demoted, so add another Owner first. Every change is audited."
    },
    {
      "title": "3. Read the overview and publish notices",
      "description": "Overview shows KPIs (booked seats, waitlisted, checked in, trips), per-trip capacity, meal and banquet totals and open cases, labelled with source and update time. On Notices publish bilingual notices (general, important, critical) and withdraw them; publishing and withdrawing are audited."
    },
    {
      "title": "4. Work help desk cases and email push",
      "description": "Help desk lists all cases and lets you set the status to new, in progress, resolved or closed. To email people who hold a shuttle booking, open the Notice email tab in operations: press Preview recipients to see the count and masked samples, and only then Confirm and send. Each recipient receives each notice at most once, up to 500 per send, and a send cannot be undone."
    },
    {
      "title": "5. Operations tools (Owner only)",
      "description": "At /events/:slug/admin/operations: volunteer shifts (create shifts and assign volunteers by email; volunteers see only their own shifts), posters and equipment (import the official layout one line per \"position code, title, presenter email\"; a duplicate position rejects the whole batch; approve or reject equipment requests), and speaker files (add speakers; only PDF and PPTX up to 30 MB, always quarantined first, so download and scan locally with antivirus before approving)."
    },
    {
      "title": "6. Meals and emergency contact data",
      "description": "The dashboard shows only headcounts per dietary category; Owner and Manager can export details and every export is audited. Rider mobile numbers can be loaded only by an Owner on the passenger list, each load logged. Meal, contact and reminder records are deleted automatically 30 days after the event."
    }
  ],
  "afterward": [
    "Members, notices, case status and exports all leave audit records",
    "The event page shows check-in, list and console entries only to people with the capability (real authorization stays on the server)"
  ],
  "nextSteps": "For on-site check-in see \"How to check in riders and use the passenger list on event day\".",
  "warning": "The last Owner is protected by the database and cannot be removed or demoted. A notice email cannot be recalled after sending, so always preview the recipients first.",
  "faq": [
    {
      "q": "Can a Manager check in riders?",
      "a": "No. Check-in and the passenger list belong to Scanner and Owner; Manager handles notices, email push, meal export and cases."
    },
    {
      "q": "I organize a competition. Am I automatically an event Owner?",
      "a": "No. Event membership is separate from competition permissions; only people added to that event, plus platform super admins, have event access."
    },
    {
      "q": "Why can I not see Event admin?",
      "a": "It appears only for users who are staff on at least one published event; ask the event Owner to confirm they added you with your verified email."
    }
  ],
  "figures": [
    {
      "name": "admin-overview",
      "alt": "Organizer console overview: KPIs, per-trip capacity and meal totals, with source and update time",
      "caption": "Organizer console overview: KPIs, per-trip capacity and meal totals, with source and update time"
    },
    {
      "name": "admin-members",
      "alt": "Members tab: add members and roles; people not yet registered show as pending",
      "caption": "Members tab: add members and roles; people not yet registered show as pending"
    }
  ]
}
